import { trackEvent } from './analytics';

export interface AffiliateLinkContext {
    page: string;
    cluster: string;
    language: string;
    market?: string;
}

export interface AffiliateLinkMetadata {
    provider: string;
    destinationHost: string;
    destinationType: 'checkout' | 'pricing' | 'signup' | 'product';
}

const SITE_URL = 'https://traffic-creator.com';

const PROVIDER_DOMAINS: Record<string, string> = {
    'seranking.com': 'SE Ranking',
    'store.payproglobal.com': 'Ranktracker',
    'ranktracker.com': 'Ranktracker',
    'localo.com': 'Localo',
    'linkwhisper.com': 'Link Whisper',
    'seowriting.ai': 'SEOWritingAI',
    'usermaven.com': 'Usermaven',
    'plerdy.com': 'Plerdy',
    'snowseo.com': 'SnowSEO',
    'seojuice.com': 'SEOJuice',
    'getgenie.ai': 'GetGenie',
    'contentstudio.io': 'ContentStudio',
    'llmpulse.ai': 'LLM Pulse',
    'wpforms.com': 'WPForms',
    'wpcode.com': 'WPCode',
    'wpcodelibrary.com': 'WPCode Library',
    'wpvivid.com': 'WPvivid',
    'proxyscrape.com': 'ProxyScrape',
    'webshare.io': 'Webshare',
    'dataimpulse.com': 'DataImpulse',
    'geonode.com': 'GeoNode',
    'gohighlevel.com': 'HighLevel',
    'finom.co': 'Finom',
    'xolo.io': 'Xolo',
};

const AFFILIATE_QUERY_PARAMETERS = new Set([
    'aff',
    'affiliate',
    'affiliate_id',
    'aff_id',
    'fpr',
    'fp_ref',
    'ref',
    'referral_code',
    'rui',
    'via',
    'fnm_refid',
]);

const LANGUAGE_MARKETS: Record<string, string> = {
    en: 'US',
    de: 'DE',
    es: 'ES',
    fr: 'FR',
    pt: 'BR',
    it: 'IT',
    ja: 'JP',
    zh: 'CN',
    hi: 'IN',
};

const normalizeHostname = (hostname: string): string => hostname.toLowerCase().replace(/^www\./, '');

const getProvider = (hostname: string): string | null => {
    const normalizedHostname = normalizeHostname(hostname);
    const domain = Object.keys(PROVIDER_DOMAINS).find(candidate => (
        normalizedHostname === candidate || normalizedHostname.endsWith(`.${candidate}`)
    ));
    return domain ? PROVIDER_DOMAINS[domain] : null;
};

const isSponsoredRel = (rel: string): boolean => (
    rel.toLowerCase().split(/\s+/).includes('sponsored')
);

const hasAffiliatePath = (url: URL): boolean => (
    /\/(?:ref|affiliate)\/[^/?#]+/i.test(url.pathname)
    || /^\/r\/[^/?#]+/i.test(url.pathname)
);

const hasAffiliateQuery = (url: URL, provider: string | null): boolean => {
    if (provider === 'SE Ranking' && url.searchParams.has('ga')) return true;
    if (
        provider === 'Ranktracker'
        && Array.from(url.searchParams.keys()).some(parameter => parameter.toLowerCase().includes('agreement-id'))
    ) return true;
    return Array.from(url.searchParams.keys()).some(parameter => (
        AFFILIATE_QUERY_PARAMETERS.has(parameter.toLowerCase())
    ));
};

const getDestinationType = (url: URL): AffiliateLinkMetadata['destinationType'] => {
    if (normalizeHostname(url.hostname) === 'store.payproglobal.com' || /\/checkout\b/i.test(url.pathname)) {
        return 'checkout';
    }
    if (/\/(?:pricing|subscription)(?:\/|$)/i.test(url.pathname)) return 'pricing';
    if (/\/(?:sign-?up|register|trial)(?:\/|$)/i.test(url.pathname)) return 'signup';
    return 'product';
};

export const getAffiliateMarket = (language: string): string => {
    const normalizedLanguage = String(language || 'en').toLowerCase().split('-')[0];
    return LANGUAGE_MARKETS[normalizedLanguage] || normalizedLanguage.toUpperCase();
};

export const detectAffiliateLink = (href: string, rel = ''): AffiliateLinkMetadata | null => {
    let url: URL;
    try {
        url = new URL(href, SITE_URL);
    } catch {
        return null;
    }

    const normalizedHostname = normalizeHostname(url.hostname);
    const provider = getProvider(normalizedHostname);
    const sponsored = isSponsoredRel(rel);
    const affiliateSignal = sponsored || hasAffiliatePath(url) || hasAffiliateQuery(url, provider);

    if (!affiliateSignal || normalizedHostname === 'traffic-creator.com') return null;

    return {
        provider: provider || normalizedHostname,
        destinationHost: normalizedHostname,
        destinationType: getDestinationType(url),
    };
};

export const trackAffiliateLinkClick = (
    anchor: HTMLAnchorElement,
    container: HTMLElement,
    context: AffiliateLinkContext,
): boolean => {
    const metadata = detectAffiliateLink(anchor.href, anchor.rel);
    if (!metadata) return false;

    const affiliateAnchors = Array.from(container.querySelectorAll<HTMLAnchorElement>('a[href]'))
        .filter(candidate => detectAffiliateLink(candidate.href, candidate.rel));
    const explicitPosition = Number.parseInt(anchor.dataset.affiliatePosition || '', 10);
    const calculatedPosition = affiliateAnchors.indexOf(anchor) + 1;
    const linkPosition = Number.isFinite(explicitPosition) && explicitPosition > 0
        ? explicitPosition
        : Math.max(calculatedPosition, 1);
    const semanticPosition = anchor.dataset.affiliatePosition?.trim() || `link_${linkPosition}`;
    const cluster = anchor.dataset.affiliateCluster?.trim() || context.cluster;
    const language = anchor.dataset.affiliateLanguage?.trim() || context.language;
    const market = anchor.dataset.affiliateMarket?.trim() || context.market || getAffiliateMarket(language);

    trackEvent('affiliate_click', {
        provider: metadata.provider,
        page: context.page,
        link_position: linkPosition,
        position: semanticPosition,
        cluster,
        destination_type: metadata.destinationType,
        destination_host: metadata.destinationHost,
        language,
        market,
    });

    return true;
};
